import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { cleanHtmlToText } from "@/lib/clean-html-text";
import { getVendorIdsForProfile, getPrimaryVendorForProfile, isVendorMember } from "./vendor-membership.server";


const slugify = (s: string) =>
  s.toLowerCase().trim()
    .replace(/[čć]/g, "c").replace(/[š]/g, "s").replace(/[ž]/g, "z").replace(/[đ]/g, "d")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

async function getSupabaseAdmin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

export const createVendor = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({
      store_name: z.string().trim().min(2).max(100),
      city: z.string().trim().max(100).optional().nullable(),
      description: z.string().trim().max(2000).optional().nullable(),
      slug: z.string().trim().max(80).optional().nullable(),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { userId, claims } = context;
    const supabaseAdmin = await getSupabaseAdmin();

    // Ensure profile exists
    let { data: profile } = await supabaseAdmin
      .from("profiles")
      .select("id, email")
      .eq("auth_user_id", userId)
      .maybeSingle();

    if (!profile) {
      const { data: created, error: pErr } = await supabaseAdmin
        .from("profiles")
        .insert({ auth_user_id: userId, email: (claims.email as string) ?? "" })
        .select("id, email")
        .single();
      if (pErr) throw new Error(pErr.message);
      profile = created;
    }

    // Existing vendor? Return it instead of failing.
    const { data: existing } = await supabaseAdmin
      .from("vendors").select("id, slug").eq("profile_id", profile!.id).maybeSingle();
    if (existing) return { id: existing.id, slug: existing.slug ?? "" };

    // Unique slug
    const base = slugify(data.slug || data.store_name) || `trgovina-${Date.now()}`;
    let slug = base;
    for (let i = 2; i < 50; i++) {
      const { data: hit } = await supabaseAdmin.from("vendors").select("id").eq("slug", slug).maybeSingle();
      if (!hit) break;
      slug = `${base}-${i}`;
    }

    const { data: vendor, error } = await supabaseAdmin
      .from("vendors")
      .insert({
        profile_id: profile!.id,
        store_name: data.store_name,
        slug,
        city: data.city || null,
        description: data.description || null,
        status: "active",
      })
      .select("id, slug")
      .single();
    if (error) throw new Error(error.message);

    // Assign vendor role
    await supabaseAdmin
      .from("user_roles")
      .insert({ profile_id: profile!.id, role: "vendor" });

    // 3-month free trial for newly registered vendors
    const trialStart = new Date();
    const trialEnd = new Date(trialStart);
    trialEnd.setMonth(trialEnd.getMonth() + 3);
    await supabaseAdmin.from("subscriptions").insert({
      profile_id: profile!.id,
      vendor_id: vendor.id,
      customer_email: (profile as { email?: string }).email ?? (claims.email as string) ?? "",
      product_name: "Brezplačni preizkus (3 mesece)",
      amount: 0,
      currency: "eur",
      interval: "month",
      status: "trialing",
      started_at: trialStart.toISOString(),
      current_period_start: trialStart.toISOString(),
      current_period_end: trialEnd.toISOString(),
      cancel_at_period_end: true,
    });

    return { id: vendor.id, slug: vendor.slug };
  });

export type VendorProduct = {
  id: string;
  title: string;
  slug: string;
  price: number | null;
  sale_price: number | null;
  currency: string;
  in_stock: boolean;
  stock_quantity: number | null;
  status: string;
  short_description: string | null;
  featured_image_url: string | null;
  vendor_name?: string | null;
};

async function isAdminProfile(profileId: string): Promise<boolean> {
  const supabaseAdmin = await getSupabaseAdmin();
  const { data } = await supabaseAdmin
    .from("user_roles").select("role").eq("profile_id", profileId);
  return (data ?? []).some((r) => String(r.role) === "admin");
}

export const listMyProducts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<VendorProduct[]> => {
    const { userId } = context;
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: profile } = await supabaseAdmin.from("profiles").select("id").eq("auth_user_id", userId).maybeSingle();
    if (!profile) return [];
    const isAdmin = await isAdminProfile(profile.id);

    if (isAdmin) {
      const { data, error } = await supabaseAdmin
        .from("products")
        .select("id, title, slug, price, sale_price, currency, in_stock, stock_quantity, status, short_description, featured_image_url, vendor:vendors(store_name)")
        .order("created_at", { ascending: false })
        .limit(1000);
      if (error) throw new Error(error.message);
      return (data ?? []).map((p) => ({
        id: p.id as string,
        title: p.title as string,
        slug: p.slug as string,
        price: p.price as number | null,
        sale_price: p.sale_price as number | null,
        currency: p.currency as string,
        in_stock: p.in_stock as boolean,
        stock_quantity: p.stock_quantity as number | null,
        status: p.status as string,
        short_description: p.short_description as string | null,
        featured_image_url: p.featured_image_url as string | null,
        vendor_name: ((p as unknown as { vendor: { store_name: string } | null }).vendor?.store_name) ?? null,
      }));
    }

    const vendor = await getPrimaryVendorForProfile(supabaseAdmin, profile.id);
    if (!vendor) return [];
    const { data, error } = await supabaseAdmin
      .from("products")
      .select("id, title, slug, price, sale_price, currency, in_stock, stock_quantity, status, short_description, featured_image_url")
      .eq("vendor_id", vendor.id)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as VendorProduct[];
  });


const productSchema = z.object({
  id: z.string().uuid().optional().nullable(),
  title: z.string().trim().min(2).max(200),
  slug: z.string().trim().max(120).optional().nullable(),
  price: z.number().min(0).max(1000000).optional().nullable(),
  sale_price: z.number().min(0).max(1000000).optional().nullable(),
  short_description: z.string().trim().max(500).optional().nullable(),
  description: z.string().trim().max(10000).optional().nullable(),
  featured_image_url: z.string().url().max(2000).optional().nullable(),
  gallery_urls: z.array(z.string().url().max(2000)).max(10).optional().nullable(),
  in_stock: z.boolean(),
  stock_quantity: z.number().int().min(0).max(100000).optional().nullable(),
  status: z.enum(["publish", "draft", "pending", "private"]),
  category_ids: z.array(z.string().uuid()).max(10).optional().nullable(),
});

export const upsertProduct = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => productSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: profile } = await supabaseAdmin.from("profiles").select("id").eq("auth_user_id", userId).maybeSingle();
    if (!profile) throw new Error("Profil ne obstaja.");
    const isAdmin = await isAdminProfile(profile.id);

    let vendorId: string | null = null;
    if (data.id) {
      const { data: existing } = await supabaseAdmin
        .from("products").select("vendor_id").eq("id", data.id).maybeSingle();
      if (!existing) throw new Error("Izdelek ne obstaja.");
      vendorId = existing.vendor_id as string | null;
      if (!isAdmin && !(await isVendorMember(supabaseAdmin, profile.id, vendorId as string))) {
        throw new Error("Ni dovoljenja.");
      }
    } else {
      const vendor = await getPrimaryVendorForProfile(supabaseAdmin, profile.id);
      if (!vendor) throw new Error("Najprej ustvarite trgovino.");
      vendorId = vendor.id as string;
    }

    if (!isAdmin) {
      // Subscription follows the owned vendor profile; members edit under the owner’s active subscription.
      if (!vendorId) throw new Error("Manjka ID trgovine.");
      const { data: ownedVendor } = await supabaseAdmin
        .from("vendors")
        .select("profile_id")
        .eq("id", vendorId)
        .maybeSingle();

      const billingProfileId = ownedVendor?.profile_id ?? profile.id;
      const { data: sub } = await supabaseAdmin
        .from("subscriptions")
        .select("status, current_period_end")
        .eq("profile_id", billingProfileId)
        .order("current_period_end", { ascending: false })
        .limit(1)
        .maybeSingle();
      const isActive =
        (sub?.status === "active" || sub?.status === "trialing") &&
        (!sub.current_period_end || new Date(sub.current_period_end) > new Date());
      if (!isActive) throw new Error("Za dodajanje ali urejanje izdelkov je potrebna aktivna naročnina.");
    }


    const slug = slugify(data.slug || data.title) || `izdelek-${Date.now()}`;

    const payload = {
      vendor_id: vendorId,
      title: data.title,
      slug,
      price: data.price ?? null,
      sale_price: data.sale_price ?? null,
      short_description: data.short_description ? cleanHtmlToText(data.short_description, 500) : null,
      description: data.description ? cleanHtmlToText(data.description, 10000) : null,
      featured_image_url: data.featured_image_url || null,
      gallery_urls: data.gallery_urls ?? null,
      in_stock: data.in_stock,
      stock_quantity: data.stock_quantity ?? null,
      status: data.status,
    };

    let productId = data.id ?? null;
    if (productId) {
      const { error } = await supabaseAdmin.from("products").update(payload).eq("id", productId);
      if (error) throw new Error(error.message);
    } else {
      const { data: created, error } = await supabaseAdmin.from("products").insert(payload).select("id").single();
      if (error) throw new Error(error.message);
      productId = created.id;
    }

    if (data.category_ids) {
      await supabaseAdmin.from("product_categories").delete().eq("product_id", productId);
      if (data.category_ids.length > 0) {
        const rows = data.category_ids.map((cid) => ({ product_id: productId!, category_id: cid }));
        const { error: cErr } = await supabaseAdmin.from("product_categories").insert(rows);
        if (cErr) throw new Error(cErr.message);
      }
    }

    return { id: productId };
  });

export const deleteProduct = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: profile } = await supabaseAdmin.from("profiles").select("id").eq("auth_user_id", userId).maybeSingle();
    if (!profile) throw new Error("Profil ne obstaja.");
    const isAdmin = await isAdminProfile(profile.id);
    if (!isAdmin) {
      const { data: existing } = await supabaseAdmin.from("products").select("vendor_id").eq("id", data.id).maybeSingle();
      if (!existing || !(await isVendorMember(supabaseAdmin, profile.id, existing.vendor_id as string))) {
        throw new Error("Ni dovoljenja.");
      }
    }
    const { error } = await supabaseAdmin.from("products").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });


export const getProductForEdit = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: profile } = await supabaseAdmin.from("profiles").select("id").eq("auth_user_id", userId).maybeSingle();
    if (!profile) throw new Error("Profil ne obstaja.");

    const isAdmin = await isAdminProfile(profile.id);
    const { data: existing } = await supabaseAdmin.from("products").select("vendor_id").eq("id", data.id).maybeSingle();
    if (!existing) throw new Error("Izdelek ne obstaja.");

    if (!isAdmin && !(await isVendorMember(supabaseAdmin, profile.id, existing.vendor_id as string))) {
      throw new Error("Ni dovoljenja.");
    }

    const { data: product, error } = await supabaseAdmin
      .from("products")
      .select("id, title, slug, price, sale_price, currency, in_stock, stock_quantity, status, short_description, description, featured_image_url, gallery_urls")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    const { data: cats } = await supabaseAdmin
      .from("product_categories")
      .select("category_id")
      .eq("product_id", data.id);
    return { product, category_ids: (cats ?? []).map((c: { category_id: string }) => c.category_id) };
  });


export const listCategories = createServerFn({ method: "GET" })
  .handler(async () => {
    const supabaseAdmin = await getSupabaseAdmin();
    const { data, error } = await supabaseAdmin
      .from("categories")
      .select("id, slug, name")
      .order("name");
    if (error) throw new Error(error.message);
    return data ?? [];
  });

// Slovenski IBAN: SI56 + 15 znakov (skupaj 19), črka/cifra brez presledkov
const SI_IBAN_RE = /^SI56\d{15}$/;

export function isVendorReadyForPayouts(v: {
  address_1?: string | null;
  city?: string | null;
  postcode?: string | null;
  iban?: string | null;
  account_holder?: string | null;
} | null | undefined): boolean {
  if (!v) return false;
  const iban = (v.iban ?? "").replace(/\s+/g, "").toUpperCase();
  return Boolean(
    v.address_1 && v.city && v.postcode &&
    v.account_holder && SI_IBAN_RE.test(iban),
  );
}

const vendorProfileSchema = z.object({
  store_name: z.string().trim().min(2).max(100),
  description: z.string().trim().max(5000).optional().nullable(),
  city: z.string().trim().max(100).optional().nullable(),
  country: z.string().trim().max(100).optional().nullable(),
  address_1: z.string().trim().max(200).optional().nullable(),
  postcode: z.string().trim().max(20).optional().nullable(),
  iban: z.string().trim().max(34).optional().nullable()
    .transform((v) => (v ? v.replace(/\s+/g, "").toUpperCase() : v))
    .refine((v) => !v || SI_IBAN_RE.test(v), { message: "TRR mora biti v obliki SI56 + 15 cifer." }),
  account_holder: z.string().trim().max(200).optional().nullable(),
  bank_name: z.string().trim().max(200).optional().nullable(),
  image_url: z.string().url().max(2000).optional().nullable(),
  banner_url: z.string().url().max(2000).optional().nullable(),
  instagram: z.string().url().max(500).optional().nullable(),
  youtube: z.string().url().max(500).optional().nullable(),
  linkedin: z.string().url().max(500).optional().nullable(),
  facebook: z.string().url().max(500).optional().nullable(),
  tiktok: z.string().url().max(500).optional().nullable(),
  shipping_policy: z.string().trim().max(5000).optional().nullable(),
  refund_policy: z.string().trim().max(5000).optional().nullable(),
  message_to_buyers: z.string().trim().max(2000).optional().nullable(),
  shipping_fee: z.number().min(0).max(1000).optional().nullable(),
  free_shipping_over: z.number().min(0).max(100000).optional().nullable(),
  public_phone: z.string().trim().min(6, { message: "Telefonska številka je obvezna." }).max(40),
  phone_public: z.boolean().optional().default(false),
});


export const getMyVendor = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context;
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: profile } = await supabaseAdmin.from("profiles").select("id").eq("auth_user_id", userId).maybeSingle();
    if (!profile) return null;
    const vendor = await getPrimaryVendorForProfile(supabaseAdmin, profile.id);
    if (!vendor) return null;
    const { data } = await supabaseAdmin
      .from("vendors")
      .select("id, profile_id, slug, store_name, description, banner_url, image_url, address_1, city, postcode, country, state, instagram, youtube, linkedin, iban, account_holder, bank_name, status, message_to_buyers, cancellation_policy, refund_policy, shipping_policy, created_at, updated_at, shipping_fee, free_shipping_over, verified_at, facebook, tiktok, public_email, public_phone, phone_public")
      .eq("id", vendor.id)
      .maybeSingle();
    return data;
  });


export const updateMyVendor = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => vendorProfileSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: profile } = await supabaseAdmin.from("profiles").select("id").eq("auth_user_id", userId).maybeSingle();
    if (!profile) throw new Error("Profil ne obstaja.");
    const isAdmin = await isAdminProfile(profile.id);
    const vendor = await getPrimaryVendorForProfile(supabaseAdmin, profile.id);
    if (!vendor) throw new Error("Trgovina ne obstaja.");
    if (!isAdmin) {
      const { data: ownedVendor } = await supabaseAdmin
        .from("vendors")
        .select("profile_id")
        .eq("id", vendor.id)
        .maybeSingle();
      const billingProfileId = ownedVendor?.profile_id ?? profile.id;
      const { data: sub } = await supabaseAdmin
        .from("subscriptions")
        .select("status, current_period_end")
        .eq("profile_id", billingProfileId)
        .order("current_period_end", { ascending: false })
        .limit(1)
        .maybeSingle();
      const isActive =
        (sub?.status === "active" || sub?.status === "trialing") &&
        (!sub.current_period_end || new Date(sub.current_period_end) > new Date());
      if (!isActive) throw new Error("Za urejanje trgovine je potrebna aktivna naročnina.");
    }
    const { error } = await supabaseAdmin.from("vendors").update({
      store_name: data.store_name,
      description: data.description || null,
      city: data.city || null,
      country: data.country || null,
      address_1: data.address_1 || null,
      postcode: data.postcode || null,
      iban: data.iban || null,
      account_holder: data.account_holder || null,
      bank_name: data.bank_name || null,
      image_url: data.image_url || null,
      banner_url: data.banner_url || null,
      instagram: data.instagram || null,
      youtube: data.youtube || null,
      linkedin: data.linkedin || null,
      facebook: data.facebook || null,
      tiktok: data.tiktok || null,
      shipping_policy: data.shipping_policy || null,
      refund_policy: data.refund_policy || null,
      message_to_buyers: data.message_to_buyers || null,
      shipping_fee: data.shipping_fee ?? 0,
      free_shipping_over: data.free_shipping_over ?? null,
      public_phone: data.public_phone,
      phone_public: data.phone_public ?? false,
    } as never).eq("id", vendor.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });


export const deleteMyVendor = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context;
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: profile } = await supabaseAdmin.from("profiles").select("id").eq("auth_user_id", userId).maybeSingle();
    if (!profile) throw new Error("Profil ne obstaja.");
    const { data: vendor } = await supabaseAdmin.from("vendors").select("id, profile_id").eq("profile_id", profile.id).maybeSingle();
    if (!vendor) throw new Error("Trgovina ne obstaja.");
    await supabaseAdmin.from("products").delete().eq("vendor_id", vendor.id);
    await supabaseAdmin.from("vendor_members").delete().eq("vendor_id", vendor.id);
    const { error } = await supabaseAdmin.from("vendors").delete().eq("id", vendor.id);
    if (error) throw new Error(error.message);
    await supabaseAdmin.from("user_roles").delete().eq("profile_id", profile.id).eq("role", "vendor");
    return { ok: true };
  });



